import React, { useEffect, useState } from 'react';
import { OptImg } from '../components/ui/OptImg';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ProductCmsContent } from '../components/website/ProductCmsContent';
import { ProductPairs } from '../components/website/ProductsNav';
import { ConsultationPrompt } from '../components/website/ConsultationPrompt';
import { ProductFlipGrid } from '../components/products/ProductCategoryFlipCards';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sun, Layers, Zap, Loader2, GitCompare, Check, Heart, Eye } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import ProductComparison, { CompareFloatingButton } from '../components/ProductComparison';
import QuickViewModal from '../components/QuickViewModal';
import { useWishlist } from '../contexts/WishlistContext';
import { sanitizeHtml } from '../lib/sanitize';
import axios from 'axios';

import { FabricVisualizerSection } from '../components/website/sections/FabricVisualizerSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for categories
const ICON_MAP = {
  Sun: Sun,
  Layers: Layers,
  Zap: Zap,
};

// Default category styling
const CATEGORY_COLORS = {
  shades: 'amber',
  drapery: 'rose',
  motorization: 'blue',
};

// Category Tab Component
const CategoryTab = ({ category, isActive, onClick }) => {
  const Icon = ICON_MAP[category.icon] || Sun;
  const color = CATEGORY_COLORS[category.slug] || 'amber';
  
  return (
    <button
      onClick={onClick}
      className={`relative flex-1 group transition-all duration-300 ${isActive ? 'z-10' : ''}`}
      data-testid={`tab-${category.slug}`}
    >
      <div className={`relative overflow-hidden rounded-xl ${isActive ? 'ring-2 ring-amber-500 ring-offset-2' : ''}`}>
        {/* Background Image */}
        <div className="aspect-[16/9] relative">
          <OptImg
            sizes="(min-width: 768px) 33vw, 100vw"
            src={category.image_url || 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600'}
            alt={category.name}
            decoding="async"
            className={`w-full h-full object-cover transition-all duration-500 ${isActive ? 'scale-105' : 'grayscale-[30%] group-hover:grayscale-0'}`}
          />
          {/* Overlay */}
          <div className={`absolute inset-0 transition-all duration-300 ${isActive ? 'bg-black/30' : 'bg-black/50 group-hover:bg-black/40'}`} />
          
          {/* Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 transition-all ${isActive ? `bg-${color}-500` : 'bg-white/20 group-hover:bg-white/30'}`}>
              <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-white/90'}`} />
            </div>
            <h3 className={`text-lg font-semibold text-white transition-all ${isActive ? 'text-xl' : ''}`}>
              {category.name}
            </h3>
          </div>
        </div>
      </div>
    </button>
  );
};

// Product Card Component - Updated with wishlist and quick view
const ProductCard = ({ product, isComparing, onToggleCompare, canAddMore, onQuickView, eager = false }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const inWishlist = isInWishlist(product.product_type || product.id);
  
  // Determine the link based on whether product has a configurator
  const href = product.has_configurator 
    ? `/products/${product.product_type}` 
    : (product.cta_link || `/products/${product.product_type}`);
  
  // Get primary image
  const primaryImage = product.images?.find(img => img.is_primary) || product.images?.[0];
  const imageUrl = primaryImage?.url || 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="group relative"
    >
      {/* Action buttons - top right - hidden by default, show on hover (or always if active) */}
      <div className={`absolute top-3 right-3 z-20 flex flex-col gap-2 transition-opacity duration-200 ${
        (isComparing || inWishlist) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
      }`}>
        {/* Compare button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleCompare(product);
          }}
          disabled={!isComparing && !canAddMore}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-lg ${
            isComparing 
              ? 'bg-amber-500 text-white !opacity-100' 
              : canAddMore 
                ? 'bg-white/90 text-neutral-600 hover:bg-amber-100 hover:text-amber-600' 
                : 'bg-white/50 text-neutral-300 cursor-not-allowed'
          }`}
          title={isComparing ? 'Remove from comparison' : canAddMore ? 'Add to comparison' : 'Max 3 products'}
        >
          {isComparing ? (
            <Check className="w-4 h-4" />
          ) : (
            <GitCompare className="w-4 h-4" />
          )}
        </button>
        
        {/* Wishlist button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleWishlist(product);
          }}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-lg ${
            inWishlist 
              ? 'bg-red-500 text-white !opacity-100' 
              : 'bg-white/90 text-neutral-600 hover:bg-red-100 hover:text-red-500'
          }`}
          title={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart className={`w-4 h-4 ${inWishlist ? 'fill-current' : ''}`} />
        </button>
        
        {/* Quick View button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onQuickView(product);
          }}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-lg bg-white/90 text-neutral-600 hover:bg-blue-100 hover:text-blue-600"
          title="Quick View"
        >
          <Eye className="w-4 h-4" />
        </button>
      </div>

      <Link to={href} className="block" data-testid={`product-${product.product_type}`}>
        <div className={`bg-white rounded-xl overflow-hidden border transition-all duration-300 ${
          isComparing 
            ? 'border-amber-500 ring-2 ring-amber-200' 
            : 'border-neutral-200 hover:border-amber-300 hover:shadow-lg'
        }`}>
          {/* Product Image */}
          <div className="aspect-[4/3] relative overflow-hidden bg-neutral-100">
            <OptImg
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
              src={imageUrl.startsWith('/') ? `${API_URL}${imageUrl}` : imageUrl}
              alt={product.display_name || product.title}
              loading={eager ? undefined : 'lazy'}
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {/* Featured Badge */}
            {product.is_featured && (
              <div className="absolute top-3 left-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg">
                Featured
              </div>
            )}
            {product.base_price > 0 && (
              <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                From ${product.base_price}
              </div>
            )}
          </div>
          
          {/* Product Info */}
          <div className="p-4">
            <h4 className="font-semibold text-neutral-900 group-hover:text-amber-600 transition-colors">
              {product.display_name || product.title}
            </h4>
            <p className="text-sm text-neutral-500 mt-1 line-clamp-2 whitespace-pre-line">
              {product.short_description || product.description?.slice(0, 100)}
            </p>
            
            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-3">
                {product.features.slice(0, 3).map((feature, idx) => (
                  <span key={idx} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">
                    {feature.title}
                  </span>
                ))}
              </div>
            )}
            
            {/* CTA */}
            <div className="flex items-center gap-1 mt-4 text-sm font-medium text-amber-600 group-hover:gap-2 transition-all">
              <span>{product.has_configurator ? 'Configure' : 'Learn More'}</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

// Interactive catalog (tabs, compare, wishlist, quick view). Used by the coded page AND the builder's product_grid 'interactive' layout.
const OVERVIEW_FAQ = { '/products/shades': 'shades', '/products/drapery': 'drapery' };

export const ProductsCatalog = ({ embedded = false, navigateOnTab = !embedded, header = true, eyebrow = 'Our Collection', title = 'Window Treatments', body = 'Explore our complete range of custom window treatments, from elegant shades to smart motorized solutions.', initialCategory, flipLayout = 'classic' }) => {
  const location = useLocation();
  const grid8 = flipLayout === 'grid8' || location.pathname === '/products';
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('shades');
  
  // Product comparison state
  const [compareProducts, setCompareProducts] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  
  // Quick view state
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  const toggleCompare = (product) => {
    setCompareProducts(prev => {
      const exists = prev.find(p => p.product_type === product.product_type);
      if (exists) {
        return prev.filter(p => p.product_type !== product.product_type);
      }
      if (prev.length >= 3) {
        return prev; // Max 3 products
      }
      return [...prev, product];
    });
  };

  const removeFromCompare = (productType) => {
    setCompareProducts(prev => prev.filter(p => p.product_type !== productType && p.id !== productType));
  };

  const clearCompare = () => {
    setCompareProducts([]);
    setShowComparison(false);
  };

  // Determine active category from URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/shades')) {
      setActiveCategory('shades');
    } else if (path.includes('/drapery')) {
      setActiveCategory('drapery');
    } else if (path.includes('/motorization')) {
      setActiveCategory('motorization');
    } else {
      setActiveCategory(embedded && initialCategory ? initialCategory : 'shades'); // Default
    }
  }, [location.pathname, embedded, initialCategory]);

  // Fetch categories and products from CMS
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [categoriesRes, productsRes] = await Promise.all([
          axios.get(`${API_URL}/api/cms/categories`),
          axios.get(`${API_URL}/api/cms/products`)
        ]);
        setCategories(categoriesRes.data);
        setProducts(productsRes.data);
      } catch (error) {
        console.error('Failed to fetch CMS data:', error);
        // Fallback to empty arrays
        setCategories([]);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter products by category
  const getProductsByCategory = (categorySlug) => {
    return products.filter(p => p.category_slug === categorySlug && p.is_active);
  };

  const handleCategoryChange = (categorySlug) => {
    setActiveCategory(categorySlug);
    if (navigateOnTab) navigate(`/products/${categorySlug}`);
  };

  const currentProducts = getProductsByCategory(activeCategory);
  const currentCategory = categories.find(c => c.slug === activeCategory) || {
    name: activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1),
    description: ''
  };

  return (
    <>
      <div className={embedded ? '' : 'min-h-screen bg-neutral-50 pt-24 pb-16'} data-testid="products-page">
        <div className="container-xl">
          {header && (
            <div className="text-center mb-10">
              <span className="label-overline text-amber-600">{eyebrow}</span>
              <h1 className="mt-2 !text-4xl md:!text-5xl font-serif">{title}</h1>
              <p className="text-neutral-500 mt-3 max-w-2xl mx-auto">{body}</p>
            </div>
          )}

          {/* Main category flip cards (Products landing only) */}
          {grid8 && <ProductFlipGrid />}

          {!grid8 && (<>
          {/* Category Tabs - From CMS */}
          {loading ? (
            <div className="flex gap-4 mb-10 max-w-4xl mx-auto" data-testid="category-tabs-skeleton">
              {[1, 2, 3].map((i) => <div key={i} className="flex-1 aspect-[16/9] rounded-xl bg-neutral-200 animate-pulse" />)}
            </div>
          ) : categories.length > 0 ? (
            <div className="flex gap-4 mb-10 max-w-4xl mx-auto">
              {categories.map((category) => (
                <CategoryTab
                  key={category.slug}
                  category={category}
                  isActive={activeCategory === category.slug}
                  onClick={() => handleCategoryChange(category.slug)}
                />
              ))}
            </div>
          ) : null}

          {/* Category Description - From CMS */}
          <motion.div
            key={activeCategory}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-serif text-neutral-900">{currentCategory.name}</h2>
            <div 
              className="text-neutral-500 mt-1 prose prose-sm max-w-none [&>p]:my-1"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentCategory.description || '') }}
            />
          </motion.div>

          {/* Products Grid - From CMS */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-neutral-200 rounded-xl aspect-[4/3]" />
                  <div className="mt-4 h-4 bg-neutral-200 rounded w-3/4" />
                  <div className="mt-2 h-3 bg-neutral-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : currentProducts.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-neutral-200">
              <p className="text-neutral-500">No products found in this category.</p>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {currentProducts.map((product, idx) => (
                  <ProductCard eager={idx < 4} 
                    key={product.id} 
                    product={product}
                    isComparing={compareProducts.some(p => p.product_type === product.product_type)}
                    onToggleCompare={toggleCompare}
                    canAddMore={compareProducts.length < 3}
                    onQuickView={setQuickViewProduct}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          )}
          {!embedded && (
            <nav className="mt-16 border-t border-neutral-200 pt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm" aria-label="Explore by project type" data-testid="project-type-links">
              <span className="label-overline text-neutral-500">Explore by project</span>
              {[['Residential', '/products/residential'], ['Commercial Grade', '/products/commercial'], ['Architectural', '/products/architectural'], ['Fabric Swatches', '/fabrics']].map(([n, to]) => (
                <Link key={to} to={to} className="text-neutral-800 underline-offset-4 hover:underline hover:text-amber-700" data-testid={`project-link-${to.split('/').pop()}`}>{n}</Link>
              ))}
            </nav>
          )}
          </>)}

          {/* Motorization CTA for non-motorization categories */}
          {activeCategory !== 'motorization' && (
            <div className="mt-16 bg-gradient-to-r from-neutral-900 to-neutral-800 rounded-2xl p-8 md:p-12 text-center">
              <Zap className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl md:text-3xl font-serif text-white mb-3">
                Go Smart with Motorization
              </h3>
              <p className="text-neutral-300 max-w-xl mx-auto mb-6">
                Upgrade any of our shades or drapes with smart motorization. Control with your phone, voice, or home automation system.
              </p>
              <Link 
                to="/motorization"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Explore Motorization <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {/* CTA for motorization category - link to main motorization page */}
          {activeCategory === 'motorization' && (
            <div className="mt-16 bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 md:p-12 text-center">
              <Zap className="w-12 h-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-2xl md:text-3xl font-serif text-white mb-3">
                Go Smart with Motorization
              </h3>
              <p className="text-neutral-300 max-w-xl mx-auto mb-6">
                Learn more about smart home integration, voice control, and the technology behind our motorized solutions.
              </p>
              <Link 
                to="/motorization"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Explore Motorization <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {OVERVIEW_FAQ[location.pathname.replace(/\/+$/, '')] && <ProductPairs kind={OVERVIEW_FAQ[location.pathname.replace(/\/+$/, '')]} />}
      {OVERVIEW_FAQ[location.pathname.replace(/\/+$/, '')] && <ProductCmsContent slug={OVERVIEW_FAQ[location.pathname.replace(/\/+$/, '')]}><ConsultationPrompt /></ProductCmsContent>}

      {/* Fabric Swatch Preview */}
      <FabricVisualizerSection section={{
        props: {
          eyebrow: 'Fabric Library',
          title: 'Find Your Perfect Fabric',
          body: 'Browse our fabrics by color, collection and type. Select a swatch to see a close-up of the texture, then configure it or order a sample.',
        },
        layout: 'default',
      }} />

      {/* Floating Compare Button */}
      <CompareFloatingButton 
        count={compareProducts.length} 
        onClick={() => setShowComparison(true)} 
      />

      {/* Comparison Modal */}
      <ProductComparison
        selectedProducts={compareProducts}
        onRemove={removeFromCompare}
        onClear={clearCompare}
        isOpen={showComparison}
        onClose={() => setShowComparison(false)}
      />

      {/* Quick View Modal */}
      <QuickViewModal
        product={quickViewProduct}
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
      />
    </>
  );
};

const PAGE_H1 = { '/products': 'Custom Window Treatments', '/products/shades': 'Custom Shades', '/products/drapery': 'Custom Drapery' };
export const ProductsPage = () => {
  const { pathname } = useLocation();
  return <Layout><ProductsCatalog title={PAGE_H1[pathname.replace(/\/+$/, '')] || 'Window Treatments'} /></Layout>;
};
